import React, { useState } from 'react';
import { ArrowUpRight, ArrowRight, Users, Receipt, ArrowDownLeft, Activity } from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '../components/ui/button';
import { BillRow, Period, useReport, money, dateTime } from './api';
import { BillRows, Empty, LoadState, Notice } from './shared';
type Totals = {
  splitMinor: number;
  finishedBills: number;
  repaidMinor: number;
  repaidShares: number;
  activeUsers: number;
  newUsers: number;
  outstandingMinor: number;
};
type RetentionPoint = {
  date: string;
  activeUsers: number;
  newUsers: number;
  returningUsers: number;
  previousActiveUsers: number | null;
  retentionRate: number | null;
};
type OverviewData = {
  asOf: string;
  dataAsOf: string;
  currencies: string[];
  current: Totals;
  previous: Totals;
  series: Array<
    Totals & RetentionPoint & {
      date: string;
    }
  >;
  weeklyRetention: RetentionPoint[];
  attention: {
    total: number;
    bills: BillRow[];
  };
  processing: {
    total: number;
    successful: number;
    failed: number;
    inProgress: number;
    stalled: number;
    unknown: number;
    successRate: number | null;
    reviewed: number;
    accurate: number;
    reviewAccuracy: number | null;
    auditedBills: number;
    p95Ms: number | null;
    durationSamples: number;
    models: {
      model: string;
      attempts: number;
      costUsd: number;
      priced: number;
    }[];
  };
  dataNotes: {
    warnings: string[];
    estimatedPublishDates: number;
    undatedPaidShares: number;
    invalidAmounts: number;
    legacyCurrencyBills: number;
  };
};
const rate = (value: number | null) => (value === null ? '—' : `${(value * 100).toFixed(1)}%`);
const change = (current: number, before: number) =>
  before === 0
    ? current
      ? 'No activity in the previous period'
      : 'No change from the previous period'
    : `${current >= before ? '+' : ''}${Math.round(((current - before) / before) * 100)}% vs previous period`;
export default function Overview({
  period,
  refresh,
  retry,
  onBill,
  onUser,
  onReceipts,
  children,
}: {
  children?: React.ReactNode;
  period: Period;
  refresh: number;
  retry: () => void;
  onBill: (id: string) => void;
  onUser: (id: string) => void;
  onReceipts: (filter: string) => void;
}) {
  const [currency, setCurrency] = useState('USD'),
    [cadence, setCadence] = useState('daily');
  const { data, loading, error } = useReport<OverviewData>(
    '/overview',
    {
      ...period,
      currency,
    },
    refresh
  );
  const points = data?.series || [];
  const series =
    cadence === 'daily'
      ? points
      : Object.values(
          points.reduce<
            Record<
              string,
              Totals & {
                date: string;
              }
            >
          >((all, point) => {
            const day = new Date(point.date);
            day.setUTCDate(day.getUTCDate() - ((day.getUTCDay() + 6) % 7));
            const key = day.toISOString().slice(0, 10);
            all[key] = all[key]
              ? {
                  ...all[key],
                  splitMinor: all[key].splitMinor + point.splitMinor,
                  repaidMinor: all[key].repaidMinor + point.repaidMinor,
                  finishedBills: all[key].finishedBills + point.finishedBills,
                }
              : {
                  ...point,
                  date: key,
                };
            return all;
          }, {})
        );
  const retention = cadence === 'daily' ? points : data?.weeklyRetention || [];
  const latest = retention[retention.length - 1];
  return (
    <div className="admin-stack admin-overview">
      <LoadState loading={loading} error={error} retry={retry} />
      {data && (
        <>
          {data.dataNotes.warnings.map((w) => (
            <Notice key={w}>{w}</Notice>
          ))}
          <div className="admin-section-heading">
            <div>
              <span className="admin-eyebrow">BILL ACTIVITY</span>
              <h2>Splits and repayments</h2>
            </div>
            <label className="admin-inline-label">
              Money shown in{' '}
              <select
                aria-label="Reporting currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                {data.currencies.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="admin-metrics admin-retention-metrics">
            <Metric
              icon={<ArrowUpRight size={18} />}
              label="Money split"
              value={money(data.current.splitMinor, currency)}
              detail={change(data.current.splitMinor, data.previous.splitMinor)}
              tone="violet"
            />
            <Metric
              icon={<Receipt size={18} />}
              label="Bills finished"
              value={data.current.finishedBills.toLocaleString()}
              detail={`${currency} bills · ${change(data.current.finishedBills, data.previous.finishedBills)}`}
              tone="blue"
            />
            <Metric
              icon={<ArrowDownLeft size={18} />}
              label="Marked repaid"
              value={money(data.current.repaidMinor, currency)}
              detail={`${data.current.repaidShares} shares · ${change(data.current.repaidMinor, data.previous.repaidMinor)}`}
              tone="mint"
            />
            <Metric
              icon={<Users size={18} />}
              label="Active users"
              value={data.current.activeUsers.toLocaleString()}
              detail={`${data.current.newUsers} new accounts in this period`}
              tone="peach"
            />
            <Metric
              icon={<Activity size={18} />}
              label="User retention"
              value={rate(latest?.retentionRate ?? null)}
              detail={latest ? `${latest.returningUsers} returning · ${latest.previousActiveUsers === null
                ? 'No previous bucket' : `${latest.previousActiveUsers} active last ${cadence === 'weekly' ? 'week' : 'day'}`} · ${latest.date} UTC` : 'No activity recorded'}
              tone="violet"
            />
          </div>
          <div className="admin-overview-grid admin-top-charts">
            <section className="admin-panel admin-chart-panel">
              <div className="admin-section-heading">
                <div>
                  <h3>Split & repaid</h3>
                  <p>Finished bill amounts and shares marked paid</p>
                </div>
                <select
                  aria-label="Chart interval"
                  value={cadence}
                  onChange={(e) => setCadence(e.target.value)}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              <div className="admin-chart-legend">
                <span>
                  <i className="violet" />
                  Money split
                </span>
                <span>
                  <i className="mint" />
                  Marked repaid
                </span>
                <span>{currency}</span>
              </div>
              <div
                className="admin-chart"
                role="img"
                aria-label={`Money split and marked repaid by ${cadence === 'daily' ? 'day' : 'week'}. Exact values in the chart data table below.`}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={series}
                    margin={{
                      top: 10,
                      right: 12,
                      left: 0,
                      bottom: 0,
                    }}
                  >
                    <CartesianGrid vertical={false} stroke="var(--vs-border)" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => d.slice(5).replace('-', '/')}
                      axisLine={false}
                      tickLine={false}
                      minTickGap={32}
                      tick={{
                        fill: 'var(--vs-text-muted)',
                        fontSize: 11,
                      }}
                    />
                    <YAxis
                      width={64}
                      tickFormatter={(v) => money(v, currency).replace(/\.00$/, '')}
                      axisLine={false}
                      tickLine={false}
                      tick={{
                        fill: 'var(--vs-text-muted)',
                        fontSize: 11,
                      }}
                    />
                    <Tooltip
                      formatter={(v, name) => [
                        money(Number(v), currency),
                        name === 'splitMinor' ? 'Money split' : 'Marked repaid',
                      ]}
                      labelFormatter={(label) =>
                        `${cadence === 'weekly' ? 'Week of ' : ''}${label} · UTC`
                      }
                    />
                    <Line
                      type="linear"
                      dataKey="splitMinor"
                      stroke="var(--vs-selection)"
                      strokeWidth={2.5}
                      dot={false}
                      isAnimationActive={false}
                    />
                    <Line
                      type="linear"
                      dataKey="repaidMinor"
                      stroke="var(--vs-progress)"
                      strokeWidth={2.5}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <details className="admin-chart-data">
                <summary>View chart data</summary>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>{cadence === 'weekly' ? 'Week of' : 'Date'} (UTC)</th>
                      <th>Money split</th>
                      <th>Marked repaid</th>
                      <th>Bills finished</th>
                    </tr>
                  </thead>
                  <tbody>
                    {series.map((s) => (
                      <tr key={s.date}>
                        <td>{s.date}</td>
                        <td>{money(s.splitMinor, currency)}</td>
                        <td>{money(s.repaidMinor, currency)}</td>
                        <td>{s.finishedBills}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            </section>
            <section className="admin-panel admin-chart-panel admin-retention-panel">
              <div className="admin-section-heading">
                <div>
                  <h3>User retention</h3>
                  <p>Distinct active accounts · all currencies</p>
                </div>
                <select aria-label="Retention chart interval" value={cadence}
                  onChange={(e) => setCadence(e.target.value)}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              <div className="admin-chart-legend admin-retention-legend">
                <span><i className="violet" />New</span>
                <span><i className="mint" />Returning</span>
                <span><i className="ink" />Retention %</span>
              </div>
              <div className="admin-chart" role="img" aria-label="New and returning active users and retention rate. Values are available in the chart data table below.">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={retention} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="var(--vs-border)" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} minTickGap={28}
                      axisLine={false} tickLine={false} tick={{ fill: 'var(--vs-text-muted)', fontSize: 11 }} />
                    <YAxis yAxisId="users" width={30} allowDecimals={false} axisLine={false} tickLine={false}
                      tick={{ fill: 'var(--vs-text-muted)', fontSize: 11 }} />
                    <YAxis yAxisId="rate" orientation="right" width={48} tickFormatter={(v) => `${Math.round(v * 100)}%`}
                      axisLine={false} tickLine={false} tick={{ fill: 'var(--vs-text-muted)', fontSize: 11 }} />
                    <Tooltip filterNull={false} formatter={(v, name) => [
                      name === 'retentionRate' ? rate(v == null ? null : Number(v)) : Number(v).toLocaleString(),
                      name === 'retentionRate' ? 'Retention' : name === 'newUsers' ? 'New' : 'Returning',
                    ]} labelFormatter={(label) => `${cadence === 'weekly' ? 'Week of ' : ''}${label} · UTC`} />
                    <Line yAxisId="users" type="linear" dataKey="newUsers" stroke="var(--vs-selection)"
                      strokeWidth={2.5} dot={{ r: 2 }} isAnimationActive={false} />
                    <Line yAxisId="users" type="linear" dataKey="returningUsers" stroke="var(--vs-progress)"
                      strokeWidth={2.5} dot={{ r: 2 }} isAnimationActive={false} />
                    <Line yAxisId="rate" type="linear" dataKey="retentionRate" stroke="var(--vs-text)"
                      strokeDasharray="5 3" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <details className="admin-chart-data">
                <summary>View retention chart data</summary>
                <table className="admin-table" aria-label="User retention data">
                  <thead><tr><th>{cadence === 'weekly' ? 'Week of' : 'Date'} (UTC)</th>
                    <th>Active</th><th>New</th><th>Returning</th><th>Previous active</th><th>Retention</th></tr></thead>
                  <tbody>{retention.map((point) => <tr key={point.date}>
                    <td>{point.date}</td><td>{point.activeUsers}</td><td>{point.newUsers}</td>
                    <td>{point.returningUsers}</td><td>{point.previousActiveUsers ?? '—'}</td>
                    <td>{rate(point.retentionRate)}</td>
                  </tr>)}</tbody>
                </table>
              </details>
            </section>
          </div>
        </>
      )}
      {children}
      {data && (
        <>
          <div>
            <section className="admin-panel admin-quality">
              <div className="admin-section-heading">
                <div>
                  <span className="admin-eyebrow">RECEIPT QUALITY</span>
                  <h3>Processing & accuracy</h3>
                </div>
                <Activity size={19} />
              </div>
              <div className="admin-quality-stat">
                <span>Processing success</span>
                <strong>{rate(data.processing.successRate)}</strong>
                <small>
                  {data.processing.successful} succeeded · {data.processing.failed} failed
                </small>
              </div>
              <div className="admin-quality-stat">
                <span>Reviewed accuracy</span>
                <strong>{rate(data.processing.reviewAccuracy)}</strong>
                <small>
                  {data.processing.reviewed
                    ? `${data.processing.accurate} accurate of ${data.processing.reviewed} reviewed bills`
                    : 'Review receipts to measure accuracy'}
                </small>
              </div>
              <p className="admin-muted">
                {data.processing.inProgress} processing · {data.processing.stalled} delayed ·{' '}
                {data.processing.unknown} without a recorded result
              </p>
              <Button variant="outline" onClick={() => onReceipts('unreviewed')}>
                Review receipts <ArrowRight size={16} />
              </Button>
            </section>
          </div>
          <section className="admin-panel admin-attention">
            <div className="admin-section-heading">
              <div>
                <span className="admin-eyebrow">NEEDS ATTENTION</span>
                <h3>
                  {data.attention.total
                    ? `${data.attention.total} receipt${data.attention.total === 1 ? '' : 's'} to investigate`
                    : 'The queue is clear'}
                </h3>
                <p>Failed or processing for more than 15 minutes · all dates</p>
              </div>
              <Button variant="ghost" onClick={() => onReceipts('attention')}>
                View queue <ArrowRight size={16} />
              </Button>
            </div>
            {data.attention.bills.length ? (
              <BillRows bills={data.attention.bills} onBill={onBill} onUser={onUser} />
            ) : (
              <Empty
                title="No receipt issues waiting"
                detail="Processing failures and delayed bills will appear here."
              />
            )}
          </section>
          <div className="admin-overview-grid admin-secondary-grid">
            <section className="admin-panel">
              <h3>Still to settle</h3>
              <strong className="admin-secondary-number">
                {money(data.current.outstandingMinor, currency)}
              </strong>
              <p className="admin-muted">
                Currently unpaid shares on finished bills, across all dates. Excludes the payer’s
                own share.
              </p>
            </section>
            <section className="admin-panel">
              <h3>Processing performance</h3>
              <div className="admin-fact-line">
                <span>95th percentile duration</span>
                <strong>
                  {data.processing.p95Ms === null
                    ? 'Not recorded'
                    : `${(data.processing.p95Ms / 1000).toFixed(1)} seconds`}
                </strong>
              </div>
              <p className="admin-muted">
                Based on {data.processing.durationSamples} recorded successful runs.
              </p>
              {data.processing.models.map((m) => (
                <div className="admin-fact-line" key={m.model}>
                  <span>
                    {m.model}
                    <small>
                      {m.attempts} attempts · {m.priced} with cost data
                    </small>
                  </span>
                  <strong>{m.priced ? `$${m.costUsd.toFixed(3)}` : '—'}</strong>
                </div>
              ))}
            </section>
          </div>
          <details className="admin-definitions">
            <summary>How these numbers are measured</summary>
            <div className="admin-definition-grid">
              <p>
                <strong>Money split & bills finished.</strong> Bills first finished during the
                selected period, in {currency}. Drafts are excluded. Legacy bills use their creation
                date when a finish date is unavailable ({data.dataNotes.estimatedPublishDates} in
                this period).
              </p>
              <p>
                <strong>Marked repaid.</strong> Shares currently marked paid, grouped by their
                recorded paid date. This is user-reported repayment, not bank confirmation.
                Corrections change these totals. {data.dataNotes.undatedPaidShares} paid shares
                without a date are excluded.
              </p>
              <p>
                <strong>Processing success.</strong> The current result of receipt bills created in
                this period: successful ÷ (successful + failed). Pending and unknown results are
                excluded from the rate.
              </p>
              <p>
                <strong>Reviewed accuracy.</strong> Accurate ÷ reviewed, using each bill’s latest
                extraction attempt in the period. Reviews assess the original extraction against the
                receipt. It’s a reviewed sample, not model confidence or an estimate for every bill.
              </p>
              <p>
                <strong>Active users.</strong> Registered accounts with recorded bill, app, or
                payment activity in the period. Guests are excluded. Activity and receipt metrics
                span currencies.
              </p>
              <p>
                <strong>User retention.</strong> New users are active accounts registered in the
                bucket; returning users registered before it started. Retention is returning users
                divided by distinct active users in the previous day or Monday-UTC week, not an
                overlap cohort, so it can exceed 100%. The first bucket and a zero previous count
                show —. Synthetic accounts and guests are excluded. Missing registration dates
                count as active but cannot be classified. Edge weeks include only selected dates.
              </p>
              <p>
                <strong>Data coverage.</strong> UTC dates. Money is never converted or combined
                across currencies. {data.dataNotes.legacyCurrencyBills} older bills without a
                currency use USD. {data.dataNotes.invalidAmounts} invalid amounts are omitted.
                Weekly charts start Monday; edge weeks may be partial.
              </p>
            </div>
            <p className="admin-muted">
              Snapshot: {dateTime(data.dataAsOf)}. Reports cache for up to two minutes; Refresh
              requests a new snapshot.
            </p>
          </details>
        </>
      )}
    </div>
  );
}
function Metric({
  icon,
  label,
  value,
  detail,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
  tone: string;
}) {
  return (
    <section className="admin-metric" aria-label={`${label} metric`}>
      <div className="admin-metric-heading">
        <span>{label}</span>
        <span className={`admin-metric-icon ${tone}`}>{icon}</span>
      </div>
      <strong>{value}</strong>
      <p>{detail}</p>
    </section>
  );
}
